// The activity log, rendered once and read by every role.
//
// This is the Admin timeline — day headers, a rail of coloured nodes, and each
// entry phrased as a sentence ("Anita Rao invited a tester") — now shared with
// the Project Manager, tester and developer dashboards. It reads far better than
// a grid: an audit log is a narrative, and a table of ACTION / TARGET / IP makes
// you translate every row in your head.
//
// Two things differ from the page it grew out of:
//
//   * it is backed by GET /audit/, which decides SCOPE from the caller's role —
//     superadmin sees every company, admin and PM their own, a tester or
//     developer their own actions plus anything on a project they're assigned
//     to. There is no scope prop, so no page can ask for more than it should.
//   * filters and paging run in SQL. The original pulled a hard-capped 500 rows
//     and filtered them in the browser, so the dropdowns only ever offered
//     values that happened to be in that page.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Search, Loader2, RefreshCw, ScrollText, ChevronLeft, ChevronRight,
  UserPlus, UserCog, UserX, KeyRound, ShieldOff, CreditCard, XCircle,
  Building2, Mail, CheckCircle2, Lock, Activity as ActivityIcon,
  FolderKanban, Trash2, Play, Sparkles, Bot, Globe, X, Download, ChevronDown,
  FileText, FileSpreadsheet, FileDown,
} from "lucide-react";
import { getAuditLog, exportAuditLog } from "@/api/audit";
import { cn } from "@/lib/cn";
import { formatDate, formatDateTime } from "@/lib/dates";
import { toastSuccess, toastError } from "@/lib/toast";

const PAGE_SIZE = 25;
// Every option here is a real renderer on GET /audit/export — none is a label
// for a format the server cannot produce.
const EXPORT_FORMATS = [
  { value: "csv", label: "CSV", icon: FileDown, hint: "Raw rows, opens anywhere" },
  { value: "xlsx", label: "Excel", icon: FileSpreadsheet, hint: "Filterable spreadsheet" },
  { value: "pdf", label: "PDF", icon: FileText, hint: "Printable record" },
];
const DAY_OPTIONS = [7, 30, 90];
const DATE_INPUT =
  "h-8 rounded-lg border border-slate-200 bg-white px-2 text-xs text-slate-700 " +
  "focus:border-brand-400 focus:outline-none dark:border-white/10 dark:bg-white/5 " +
  "dark:text-slate-200 [color-scheme:light] dark:[color-scheme:dark]";

// A date picker gives "YYYY-MM-DD" with no timezone, and the reader means THEIR
// day: someone in Mumbai asking for the 3rd means 3rd 00:00 IST, which is the
// 2nd 18:30 UTC. Converting here — where the browser knows the zone — keeps the
// window aligned with the clock on the wall. Sending the bare date and letting
// the server assume UTC would shift it by hours and drop events they watched
// happen.
function dayStart(d) {
  return d ? new Date(`${d}T00:00:00`).toISOString() : undefined;
}
function dayEnd(d) {
  // Inclusive: picking 1 Mar → 1 Mar means that whole day, not one instant.
  return d ? new Date(`${d}T23:59:59.999`).toISOString() : undefined;
}

const TONE = {
  sky: "bg-sky-500/12 text-sky-600 dark:text-sky-300 ring-sky-500/20",
  violet: "bg-violet-500/12 text-violet-600 dark:text-violet-300 ring-violet-500/20",
  emerald: "bg-emerald-500/12 text-emerald-600 dark:text-emerald-300 ring-emerald-500/20",
  amber: "bg-amber-500/12 text-amber-600 dark:text-amber-300 ring-amber-500/20",
  rose: "bg-rose-500/12 text-rose-600 dark:text-rose-300 ring-rose-500/20",
  slate: "bg-slate-500/12 text-slate-600 dark:text-slate-300 ring-slate-500/20",
  brand: "bg-brand-500/12 text-brand-600 dark:text-brand-300 ring-brand-500/20",
};

// Icons for the actions worth recognising at a glance. The LABEL always comes
// from the server (one registry, so a new action is named in one place) — this
// map only decides how a row looks.
const ICONS = {
  "PROJECT.CREATE": [FolderKanban, "emerald"],
  "PROJECT.UPDATE": [FolderKanban, "sky"],
  "PROJECT.DELETE": [Trash2, "rose"],
  "PROJECT.ASSIGN": [UserPlus, "emerald"],
  "PROJECT.UNASSIGN": [UserX, "amber"],
  "PROJECT.REPO_TOKEN_SET": [KeyRound, "violet"],
  "PROJECT.CRAWL_START": [Globe, "sky"],
  "TESTCASE.GENERATE_START": [Sparkles, "brand"],
  "TESTCASE.IMPORT": [Sparkles, "sky"],
  "TESTCASE.CLEAR": [Trash2, "rose"],
  "TESTCASE.PUSH_TO_EXE": [Bot, "violet"],
  "TESTCASE.PUSH_TO_EXTENSION": [Bot, "violet"],
  "RUN.ALL_START": [Play, "brand"],
  "RUN.CANCEL": [XCircle, "amber"],
  "BATCH.CREATE": [Play, "brand"],
  "BATCH.RERUN": [RefreshCw, "brand"],
  "BATCH.DEV_STATUS": [UserCog, "sky"],
  "TESTER.INVITE": [UserPlus, "sky"],
  "DEVELOPER.INVITE": [UserPlus, "sky"],
  "PROJECT_MANAGER.INVITE": [UserPlus, "sky"],
  "TESTER.INVITE_RESEND": [Mail, "sky"],
  "TESTER.UPDATE": [UserCog, "sky"],
  "TESTER.REMOVE": [UserX, "rose"],
  "MEMBER.ROLE_CHANGE": [UserCog, "violet"],
  "TESTER.TOKEN_ISSUE": [KeyRound, "violet"],
  "TESTER.TOKEN_REVOKE": [ShieldOff, "amber"],
  "INVITE.ACCEPT": [CheckCircle2, "emerald"],
  "SUBSCRIPTION.ACTIVATE": [CreditCard, "emerald"],
  "SUBSCRIPTION.CANCEL": [XCircle, "rose"],
  "COMPANY.UPDATE": [Building2, "violet"],
  "APIKEY.CREATE": [KeyRound, "violet"],
  "APIKEY.REVOKE": [ShieldOff, "amber"],
  "PROFILE.UPDATE": [UserCog, "slate"],
  "PASSWORD.CHANGE": [Lock, "slate"],
  "AUTH.LOGIN": [Lock, "slate"],
  "AUTH.GOOGLE_LOGIN": [Lock, "slate"],
};

/** Icon + tone for an action, falling back to what its VERB implies — so a new
 *  action still reads correctly without being listed above. */
function look(action) {
  if (ICONS[action]) return ICONS[action];
  const verb = String(action || "").split(".")[1] || "";
  if (/DELETE|REMOVE|CLEAR|CANCEL/.test(verb)) return [Trash2, "rose"];
  if (/REVOKE|DISABLE|STOP|PAUSE/.test(verb)) return [ShieldOff, "amber"];
  if (/CREATE|INVITE|ASSIGN|ENABLE|IMPORT|ISSUE/.test(verb)) return [UserPlus, "emerald"];
  if (/LOGIN|ACCEPT|RESET|CHANGE/.test(verb)) return [Lock, "slate"];
  return [ActivityIcon, "brand"];
}

function initials(name, email) {
  const s = name || email || "?";
  return s.split(/[\s@.]+/).map((p) => p[0]).slice(0, 2).join("").toUpperCase();
}

function relativeTime(v) {
  if (!v) return "—";
  try {
    const m = Math.floor((Date.now() - new Date(v).getTime()) / 60000);
    if (m < 1) return "just now";
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    if (d < 30) return `${d}d ago`;
    return formatDate(v);
  } catch (e) { return "—"; }
}

const fmtFull = (v) => formatDateTime(v, "");

// The audit log groups rows under a day heading; one date per group, same
// format as everywhere else.
const dayKey = (v) => formatDate(v);

const SELECT = "h-9 max-w-[13rem] truncate rounded-lg border border-slate-300 bg-white px-2.5 text-sm text-slate-700 focus:border-brand-400 focus:outline-none dark:border-white/10 dark:bg-white/5 dark:text-slate-200";

/**
 * @param {string} title     heading above the toolbar
 * @param {string} subtitle  one line saying what this role can see
 * @param {string} projectId pin the log to one project (hides the project filter)
 */
export default function AuditLogTable({ title = "Activity", subtitle, projectId }) {
  const [days, setDays] = useState(30);
  // An explicit window. Either bound alone is valid; both empty falls back to
  // the day chips.
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [actor, setActor] = useState("");
  const [action, setAction] = useState("");
  const [target, setTarget] = useState("");
  const [project, setProject] = useState("");
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");        // debounced; what's actually sent
  const [page, setPage] = useState(1);

  const [data, setData] = useState({ entries: [], total: 0, facets: {} });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(null);        // the expanded entry
  const [exporting, setExporting] = useState(null);   // the format being fetched
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  // Debounce the free-text box so typing doesn't fire a request per keystroke.
  useEffect(() => {
    const t = setTimeout(() => setQuery(search.trim()), 350);
    return () => clearTimeout(t);
  }, [search]);

  // Any change to the criteria means page 1 — staying on page 7 of a narrower
  // result set shows an empty screen and looks like "no activity".
  useEffect(() => { setPage(1); }, [days, from, to, actor, action, target, project, query]);

  // Changing a filter while on page 2+ commits the new filter before the page
  // reset lands, so two requests go out and the stale page-N one can resolve last
  // and overwrite the correct page-1 data. Only the newest request may write.
  const reqSeq = useRef(0);

  const load = useCallback(async () => {
    const seq = ++reqSeq.current;
    setLoading(true);
    setError(null);
    try {
      const res = await getAuditLog({
        days,
        date_from: dayStart(from),
        date_to: dayEnd(to),
        limit: PAGE_SIZE,
        offset: (page - 1) * PAGE_SIZE,
        actor: actor || undefined,
        action: action || undefined,
        target: target || undefined,
        project_id: projectId || project || undefined,
        search: query || undefined,
      });
      if (seq !== reqSeq.current) return;
      setData(res || { entries: [], total: 0, facets: {} });
      return true;
    } catch (e) {
      if (seq !== reqSeq.current) return;
      setError((e && e.message) || "Could not load the activity log.");
      setData({ entries: [], total: 0, facets: {} });
      return false;
    } finally {
      if (seq === reqSeq.current) setLoading(false);
    }
  }, [days, from, to, page, actor, action, target, project, projectId, query]);

  // The Refresh button called `load` directly with no confirmation of any kind
  // (#95) — a re-fetch usually returns the same rows, so a working button was
  // indistinguishable from a dead one. `load`'s return is undefined when a
  // newer request has already superseded this one; no toast either way then,
  // since that newer request is the one that will actually land.
  async function handleRefresh() {
    const ok = await load();
    if (ok === true) toastSuccess("Activity log refreshed.");
    else if (ok === false) toastError("Couldn't refresh the activity log.");
  }

  // Same filters the screen is showing (#98) — minus limit/offset, since the
  // export has its own server-side row cap instead of a page size. The FORMAT is
  // the only thing that varies: one set of rows, rendered three ways by the
  // server, so a CSV and a PDF taken a second apart can never disagree.
  async function handleExport(format) {
    if (exporting) return;
    setExporting(format);
    setMenuOpen(false);
    try {
      await exportAuditLog({
        days,
        date_from: dayStart(from),
        date_to: dayEnd(to),
        actor: actor || undefined,
        action: action || undefined,
        target: target || undefined,
        project_id: projectId || project || undefined,
        search: query || undefined,
      }, format);
      toastSuccess(`Activity log exported as ${EXPORT_FORMATS.find((f) => f.value === format).label}.`);
    } catch (e) {
      toastError(e?.message || "Couldn't export the activity log.");
    } finally {
      setExporting(null);
    }
  }

  useEffect(() => { load(); }, [load]);

  // A menu that only closes by choosing something is a trap — close it on an
  // outside click and on Escape, the two things a reader will try.
  useEffect(() => {
    if (!menuOpen) return undefined;
    const onDown = (e) => { if (!menuRef.current?.contains(e.target)) setMenuOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setMenuOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [menuOpen]);

  // Memoised: a fresh [] each render would re-group the timeline every time.
  const entries = useMemo(() => data.entries || [], [data.entries]);
  const facets = data.facets || {};
  const total = data.total || 0;
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const filtered = !!(actor || action || target || project || query || from || to);

  // Group the page into calendar days for the timeline.
  const groups = useMemo(() => {
    const map = new Map();
    entries.forEach((e) => {
      const k = dayKey(e.created_at);
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(e);
    });
    return [...map.entries()];
  }, [entries]);

  function clearFilters() {
    setActor(""); setAction(""); setTarget(""); setProject(""); setSearch("");
    setFrom("");
    setTo("");
  }

  return (
    <>
      <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h2 className="flex items-center gap-2 font-display text-lg font-semibold text-slate-900 dark:text-white">
            <ScrollText size={18} className="text-brand-500" /> {title}
          </h2>
          {subtitle && <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">{subtitle}</p>}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {/* #98: "Only Refresh is visible; no export action is shown" — this
              downloads exactly what's on screen right now, same filters, in
              whichever of the three formats the reader picks. */}
          <div className="relative" ref={menuRef}>
            <button
              type="button" onClick={() => setMenuOpen((v) => !v)} disabled={!!exporting}
              aria-haspopup="menu" aria-expanded={menuOpen}
              className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-slate-200 px-3 text-sm font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-60 dark:border-white/10 dark:text-slate-300 dark:hover:bg-white/5"
            >
              {exporting ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />}
              {exporting ? `Exporting ${exporting.toUpperCase()}…` : "Export"}
              <ChevronDown size={14} className={cn("transition-transform", menuOpen && "rotate-180")} />
            </button>
            {menuOpen && (
              <div role="menu"
                className="absolute right-0 z-20 mt-1 w-56 overflow-hidden rounded-xl border border-slate-200 bg-white py-1 shadow-soft dark:border-white/10 dark:bg-ink-900">
                {EXPORT_FORMATS.map((f) => (
                  <button
                    key={f.value} type="button" role="menuitem"
                    onClick={() => handleExport(f.value)}
                    className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-white/5"
                  >
                    <f.icon size={15} className="shrink-0 text-slate-400" />
                    <span className="min-w-0 flex-1">
                      <span className="block font-medium">{f.label}</span>
                      <span className="block text-xs text-slate-400">{f.hint}</span>
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
          <button
            type="button" onClick={handleRefresh} disabled={loading}
            className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-slate-200 px-3 text-sm font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-60 dark:border-white/10 dark:text-slate-300 dark:hover:bg-white/5"
          >
            <RefreshCw size={15} className={cn(loading && "animate-spin")} /> Refresh
          </button>
        </div>
      </div>

      {/* toolbar — every control filters in SQL, over the whole scope.
          lg:flex-wrap (#94): with up to 4 selects + the day chips + a date
          range all sharing this row at desktop width, an unbreakable
          `lg:flex-row` squeezed the search box — the only child with no
          min-width — down to near-nothing. Zooming out shrank everything's
          effective size and gave the row enough room to not need to squeeze
          it, which is why it "worked" zoomed out and not at 100%. Letting the
          row wrap, and giving the search box a floor it can't shrink past,
          fixes both symptoms of the same layout bug. */}
      <div className="card mb-6 flex flex-col gap-3 p-4 lg:flex-row lg:flex-wrap lg:items-center">
        {/* The search box gets a REAL floor and its own line until there is room
            for one.
            It was `min-w-[14rem] flex-1` in a row it shared with up to four
            selects, the day chips and two date inputs. Flex gives a `flex-1`
            child whatever is left after the others take their content width, so
            it collapsed to its 14rem floor — about 180px of usable space once
            the icon's padding is taken off — and the placeholder was cut
            mid-phrase ("…email, id" and no more). 24rem fits the sentence at the
            size it is rendered; `basis-full` keeps it on its own row below lg,
            and the wrapping row lets the filters drop underneath it when the
            window is too narrow to hold both, instead of squeezing it again. */}
        <div className="relative w-full min-w-0 basis-full lg:w-auto lg:flex-1 lg:basis-96 lg:min-w-[24rem]">
          <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search the activity log"
            /* Names the fields the server ACTUALLY matches: the action (by code
               and by its human label), actor_email, target_id and detail. It
               never matches the actor's display name or the target type, so
               wording that implied "person" or "resource" promised searches
               that could not hit. */
            placeholder="Search by email, activity, ID or details…"
            className="h-9 w-full rounded-lg border border-slate-300 bg-white pl-9 pr-3 text-sm text-slate-800 placeholder:text-slate-400 focus:border-brand-400 focus:outline-none dark:border-white/10 dark:bg-white/5 dark:text-white"
          />
        </div>
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          {/* Each dropdown appears only when there's a real choice to make —
              a tester whose scope is just themselves gets no "Everyone" list. */}
          {(facets.actors || []).length > 1 && (
            <select value={actor} onChange={(e) => setActor(e.target.value)} className={SELECT}>
              <option value="">Everyone</option>
              {facets.actors.map((a) => (
                <option key={a.value} value={a.value}>{a.label}</option>
              ))}
            </select>
          )}
          {(facets.actions || []).length > 1 && (
            <select value={action} onChange={(e) => setAction(e.target.value)} className={SELECT}>
              <option value="">All activity</option>
              {facets.actions.map((a) => (
                <option key={a.value} value={a.value}>{a.label}</option>
              ))}
            </select>
          )}
          {!projectId && (facets.projects || []).length > 0 && (
            <select value={project} onChange={(e) => setProject(e.target.value)} className={SELECT}>
              <option value="">All projects</option>
              {facets.projects.map((p) => (
                <option key={p.value} value={p.value}>{p.label}</option>
              ))}
            </select>
          )}
          {(facets.targets || []).length > 1 && (
            <select value={target} onChange={(e) => setTarget(e.target.value)} className={SELECT}>
              <option value="">All types</option>
              {facets.targets.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          )}
          {/* The chips stay for the common case — "last 30 days" shouldn't cost
              two date pickers. They grey out while an explicit window is set,
              rather than disappearing, so it's clear which one is in charge. */}
          <div className={cn("inline-flex rounded-lg border border-slate-200 p-0.5 dark:border-white/10",
                             (from || to) && "opacity-40")}>
            {DAY_OPTIONS.map((d) => (
              <button
                key={d} type="button"
                onClick={() => { setFrom(""); setTo(""); setDays(d); }}
                className={cn("rounded-md px-2.5 py-1 text-xs font-semibold transition-colors",
                  days === d && !from && !to
                    ? "bg-brand-600 text-white"
                    : "text-slate-500 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-white/5")}
              >
                {d}d
              </button>
            ))}
          </div>
          {/* min/max cross-bind the pair, so an end before its start can't be
              picked at all — the server would 422 and the reader wouldn't know why. */}
          <div className="inline-flex items-center gap-1.5">
            <input
              type="date" value={from} max={to || undefined}
              onChange={(e) => setFrom(e.target.value)}
              aria-label="From date" className={DATE_INPUT}
            />
            <span className="text-xs text-slate-400">to</span>
            <input
              type="date" value={to} min={from || undefined}
              onChange={(e) => setTo(e.target.value)}
              aria-label="To date" className={DATE_INPUT}
            />
          </div>
          {filtered && (
            <button type="button" onClick={clearFilters}
              className="inline-flex items-center gap-1 text-xs font-semibold text-brand-600 hover:text-brand-500 dark:text-brand-400">
              <X size={13} /> Clear
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="mb-5 rounded-xl bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:bg-rose-500/10 dark:text-rose-300">
          {error}
        </div>
      )}

      {loading && entries.length === 0 ? (
        <div className="flex items-center justify-center py-20 text-slate-400">
          <Loader2 size={26} className="animate-spin" />
        </div>
      ) : entries.length === 0 ? (
        <div className="card flex flex-col items-center justify-center gap-3 py-16 text-center">
          <span className="grid h-12 w-12 place-items-center rounded-2xl bg-brand-500/10 text-brand-600 dark:text-brand-300">
            <ScrollText size={24} />
          </span>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            {filtered ? "Nothing matches those filters." : "No activity in this window."}
          </p>
          {filtered && (
            <button type="button" onClick={clearFilters}
              className="text-sm font-semibold text-brand-600 hover:text-brand-500 dark:text-brand-400">
              Clear filters
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-8">
          {groups.map(([day, items]) => (
            <div key={day}>
              <div className="mb-3 flex items-center gap-3">
                <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">{day}</span>
                <span className="h-px flex-1 bg-slate-200 dark:bg-white/10" />
              </div>
              <ol className="relative ml-3 space-y-3 border-l border-slate-200 pl-6 dark:border-white/10">
                {items.map((e) => {
                  const [Icon, tone] = look(e.action);
                  const isOpen = open === e.id;
                  return (
                    <li key={e.id} className="relative">
                      <span className={cn(
                        "absolute -left-[34px] grid h-7 w-7 place-items-center rounded-full ring-4 ring-slate-50 dark:ring-ink-950",
                        TONE[tone])}>
                        <Icon size={14} />
                      </span>
                      <div
                        className={cn("card p-3.5 transition-colors",
                          "cursor-pointer hover:border-brand-300 dark:hover:border-brand-500/40",
                          isOpen && "border-brand-300 dark:border-brand-500/40")}
                        onClick={() => setOpen(isOpen ? null : e.id)}
                      >
                        <div className="flex items-start gap-3">
                          <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-gradient-to-br from-brand-400 to-brand-600 text-xs font-bold text-white">
                            {initials(e.actor_name, e.actor_email)}
                          </span>
                          <div className="min-w-0 flex-1">
                            <p className="text-sm text-slate-800 dark:text-slate-100">
                              <span className="font-semibold">
                                {e.actor_name || (e.actor_email || "Someone").split("@")[0]}
                              </span>
                              <span className="text-slate-500 dark:text-slate-400">
                                {" "}{(e.label || e.action || "").toLowerCase()}
                              </span>
                            </p>
                            <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-slate-400">
                              <span className={cn(
                                "inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-semibold ring-1 ring-inset",
                                TONE[tone])}>
                                {e.label || e.action}
                              </span>
                              {e.project_name && (
                                <span className="inline-flex items-center gap-1">
                                  <FolderKanban size={11} /> {e.project_name}
                                </span>
                              )}
                              {/* `detail` is the human part — "9 cases sent to the
                                  Exe agent", "changed: name, base_url". */}
                              {e.detail && <span className="truncate">· {e.detail}</span>}
                            </div>
                          </div>
                          {/* The exact time only used to exist in a hover title —
                              #97 wants it visible without relying on a
                              tooltip, which never appears on touch and is
                              easy to miss anyway. Relative time stays as the
                              headline; the exact stamp is now a second line,
                              not a hidden one. */}
                          <time dateTime={e.created_at || undefined} title={fmtFull(e.created_at)} className="flex shrink-0 flex-col items-end text-right">
                            <span className="text-xs text-slate-400">{relativeTime(e.created_at)}</span>
                            <span className="text-[0.6875rem] text-slate-400/70">{fmtFull(e.created_at)}</span>
                          </time>
                        </div>

                        {isOpen && (
                          <dl className="mt-3 grid grid-cols-1 gap-x-6 gap-y-2 border-t border-slate-100 pt-3 text-xs sm:grid-cols-2 dark:border-white/5">
                            {[
                              ["When", fmtFull(e.created_at)],
                              ["Who", e.actor_email],
                              ["Action", e.action],
                              ["Details", e.detail],
                              ["Project", e.project_name],
                              ["Target", e.target_id ? `${e.target || "—"} · ${e.target_id}` : e.target],
                              ["IP address", e.ip],
                            ].filter(([, v]) => v).map(([k, v]) => (
                              <div key={k} className="min-w-0">
                                <dt className="uppercase tracking-wide text-slate-400">{k}</dt>
                                <dd className="mt-0.5 break-words font-mono text-[0.6875rem] text-slate-600 dark:text-slate-300">
                                  {v}
                                </dd>
                              </div>
                            ))}
                          </dl>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ol>
            </div>
          ))}
        </div>
      )}

      {/* Real pagination against a real total — the page this replaced silently
          truncated at 500 and said "Showing 500 of 500". */}
      {total > 0 && (
        <div className="mt-6 flex flex-col items-center justify-between gap-3 text-xs text-slate-500 dark:text-slate-400 sm:flex-row">
          <span>
            Showing{" "}
            <span className="font-semibold text-slate-700 dark:text-slate-200">
              {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)}
            </span>{" "}
            of {total.toLocaleString()}
            {pages > 1 && <span className="ml-2 text-slate-400">· page {page} of {pages}</span>}
          </span>
          {pages > 1 && (
            <div className="flex items-center gap-1">
              <button
                type="button" onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1 || loading} aria-label="Previous page"
                className="grid h-8 w-8 place-items-center rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-40 dark:border-white/10 dark:text-slate-300 dark:hover:bg-white/5"
              >
                <ChevronLeft size={15} />
              </button>
              <button
                type="button" onClick={() => setPage((p) => Math.min(pages, p + 1))}
                disabled={page >= pages || loading} aria-label="Next page"
                className="grid h-8 w-8 place-items-center rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-40 dark:border-white/10 dark:text-slate-300 dark:hover:bg-white/5"
              >
                <ChevronRight size={15} />
              </button>
            </div>
          )}
        </div>
      )}
    </>
  );
}
